import * as Yup from "yup";
import { notInPast } from "../../modules/demand/controllers/quotationControllers";
import { Action, Resource } from "../../casl/constants";
import CAN from "../../casl/can";
import { USER_TYPE } from "./userConstants";
import { generateId } from "../arrayUtils";

export const sortQuotationRows = (rows) => {
  const order = [
    "pops",
    // "line-card",
    "grx-transport",
    "grx-bandwidth",
    "grx-charges",
    "sigtran",
    "diameter",
    "http",
    "dns"
  ];

  return rows?.sort(
    (a, b) => order?.indexOf(a?.group) - order?.indexOf(b?.group)
  );
};

/** ---------- Initial Values (updated to your new shape) ---------- */
export const initialValues = {
  customerLegalName: "",
  offerValidTill: "",
  minimumContractPeriod: "",
  currency: "",
  serviceConfig: {
    ipAccessStandardConnection: {
      connectionStatus: "",      // changed from "existing"
      popsDetails: [
        { id: generateId(),region: "", portSize: "" }, // both should be empty
      ]
    },
    IpxGrxTransport: {
      isIpxGrxTransport: "",    // changed
      bandwidths: [ {id: generateId(), allocatedBandwidth: "" } ]
    },
    IpxSigtran: {
      isIpxSigtran: "",    // changed
      msus: [ {id: generateId(), allocatedMsus: "" } ]
    },
    IpxDiameter: {
      isIpxDiameter: "",    // changed
      numberOfTransactions : [ {id: generateId(), transaction: "" } ]
    },
    IpxHttp: {
      isIpxHttp: "",    // changed
      transactionVolumes : [ {id: generateId(), allocatedTransactionVolumes: "" } ]
    },
    // IsIpxSigtran: "",
    // IsIpxDiameter: "",
    IsIpxDns:""
  },
  quotation: {
    columns: [],
    rows: [
      { ipxService: "Access PoP", setupCost: "0", monthlyCharges: "0", usageBased: "0", group: "pops" },
      // { ipxService: "Access per 10 G line card ", setupCost: "", monthlyCharges: "", usageBased: "", group: "line-card" },
      { ipxService: "GRX/Transport", setupCost: "0", monthlyCharges: "0", usageBased: "0", group: "grx-bandwidth" },
      { ipxService: "Charge per MBPS > allocated capacity (100 MBPS steps)", setupCost: "0", monthlyCharges: "0", usageBased: "0", group: "grx-charges" },
      { ipxService: "IPX Sigtran", setupCost: "0", monthlyCharges: "0", usageBased: "0", group: "sigtran" },
      { ipxService: "IPX Diameter", setupCost: "0", monthlyCharges: "0", usageBased: "0", group: "diameter" },
      { ipxService: "IPX HTTP2", setupCost: "0", monthlyCharges: "0", usageBased: "0", group: "http" },
      { ipxService: "IPX DNS (Included in Transport service)",setupCost: "0",monthlyCharges: "0",usageBased: "0",group: "dns"} 
    ],
  },
};

/* --------------------------------------
* Helper: Determine if row is disabled
* This mirrors your UI disable logic.
* It is defensive if context is missing.
* -------------------------------------- */
function isRowDisabled(row, ctx = {}) {
  const sc =
    (ctx && ctx.serviceConfig) ||
    (ctx.serviceConfig === undefined && ctx) ||
    {};
  const ipAccessStandardConnection = sc.ipAccessStandardConnection || {};
  const IpxGrxTransport = sc.IpxGrxTransport || {};
  const IpxSigtran = sc.IpxSigtran || {};
  const IpxDiameter = sc.IpxDiameter || {};
  const IpxHttp = sc.IpxHttp || {};
  // const IsIpxSigtran = sc.IsIpxSigtran;
  // const IsIpxDiameter = sc.IsIpxDiameter;
  const IsIpxDns = sc.IsIpxDns;

  const group = row.group || "";

  // POP disabled: require BOTH region and portSize selected for its index
  if (group === "pops") {
    const popId = row.popId;
    const pop = ipAccessStandardConnection.popsDetails?.find(
      (p) => p.id === popId
    );
    if (!pop) {
      return true; // defensive: if pop not found, treat as disabled
    }
    return !(pop.region && pop.portSize);
  }
  // GRX disabled: transport off, no cap in row, or cap not selected in bandwidths
  if (group === "grx-bandwidth") {
    const bandwidthId = row.bandwidthId;
    const bandwidths = IpxGrxTransport.bandwidths || [];
    const isSelected = bandwidths.some(
      (bw) =>
        bw.id === bandwidthId &&
        bw.allocatedBandwidth &&
        String(bw.allocatedBandwidth).trim() !== ""
    );
    return IpxGrxTransport.isIpxGrxTransport !== "yes" || !isSelected;
  }
  if (group === "sigtran") {
      const msuId = row.msuId;
      const msuList = IpxSigtran.msus || [];
      const isSelected = msuList.some(
        (msu) =>
          msu.id === msuId &&
          msu.allocatedMsus &&
          String(msu.allocatedMsus).trim() !== ""
      );
      return IpxSigtran.isIpxSigtran !== "yes" || !isSelected;
  }
  if (group === "diameter") {
      const transactionId = row.transactionId;
      const transactionList = IpxDiameter.numberOfTransactions || [];
      const isSelected = transactionList.some(
        (transaction) =>
          transaction.id === transactionId &&
          transaction.transaction &&
          String(transaction.transaction).trim() !== ""
      );
      return IpxDiameter.isIpxDiameter !== "yes" || !isSelected;
  }
  if (group === "http") {
      const transactionVolumeId = row.transactionVolumeId;
      const transactionVolumeList = IpxHttp.transactionVolumes || [];
      const isSelected = transactionVolumeList.some(
        (allocatedTransactionVolumes) =>
          allocatedTransactionVolumes.id === transactionVolumeId &&
          allocatedTransactionVolumes.allocatedTransactionVolumes &&
          String(allocatedTransactionVolumes.allocatedTransactionVolumes).trim() !== ""
      );
      return IpxHttp.isIpxHttp !== "yes" || !isSelected;
  }
  // Sigtran / Diameter disabled
  // if (group === "sigtran") return IsIpxSigtran !== "yes";
  // if (group === "diameter") return IsIpxDiameter !== "yes";
  if (group === "dns") return IsIpxDns !== "yes";

  // Line-card disabled when connection existing or empty
  // if (group === "line-card") {
  //   const status = ipAccessStandardConnection.connectionStatus;
  //   return status === "existing" || status === "";
  // }

  // Default: not disabled
  return false;
}
// ------------------ Validation(Quotation table values >= Floor Price Table value) ---------------
function findFloorRowForQuotationRow(path, floorRows = []) {
  const match = path?.match(/quotation\.rows\[(\d+)\]/);
  if (!match) return undefined;
  const rowIndex = Number(match[1]);
  return floorRows[rowIndex];
}

/* --------------------------------------
* Shared field-level validator (UPDATED):
* - when disabled → must be "--"
* - when enabled  → required & numeric (number or digit-string)
* -------------------------------------- */
function pricingFieldTest(label) {
  return Yup.string().test(
    `${label}-validation`,
    function (value) {
      const row = this.parent || {};
      const ctx = this.options?.context || {};
      const disabled = isRowDisabled(row, ctx);
      // Disabled rows: must be empty, no error
      if (disabled) {
        return value === "0" || value === 0 || value === "0.0";
      }
      // Required check
      if (!value || !value.trim()) {
        return this.createError({ message: "Required" });
      }
      // Numeric check
      if (!/^\d+(\.\d+)?$/.test(value)) {
        return this.createError({ message: "Numbers only" });
      }
      if (ctx.isQuotationMode) {
        const floorRow = findFloorRowForQuotationRow(this.path, ctx.floorRows);
        if (floorRow) {
          const floorValue = Number(floorRow[label] || 0);
          const enteredValue = Number(value);
          if (enteredValue < floorValue) {
            return this.createError({
              message: `Value must be at least ${floorValue}.`
            });
          }
        }
      }
      return true;
    }
  );
}

export const quotationSchema = (isEditable) => Yup.object({
  customerLegalName: Yup.string().required("Required"),
  offerValidTill: isEditable?notInPast.required("Required"):Yup.string().required("Required"),
  minimumContractPeriod: Yup.string().required("Required"),
  currency: Yup.string().required("Required"),

  serviceConfig: Yup.object({
    ipAccessStandardConnection: Yup.object({
      connectionStatus: Yup.string().required("Required"),
      popsDetails: Yup.array().of(
        Yup.object({
          region: Yup.string().test(
            "region-required-when-connection",
            "Required",
            function (value) {
              const parent = this.from?.[1]?.value; 
              // parent === ipAccessStandardConnection
              if (!parent?.connectionStatus) {
                // connectionStatus empty -> field disabled -> no error
                return true;
              }
              // connectionStatus filled → region required
              return Boolean(value && value.trim());
            }
          ),

          portSize: Yup.string().test(
            "portSize-required-when-connection",
            "Required",
            function (value) {
              const parent = this.from?.[1]?.value;
              if (!parent?.connectionStatus) {
                return true;
              }
              return Boolean(value && value.trim());
            }
          ),
        })
      ),
    }),
    IpxGrxTransport: Yup.object({
      isIpxGrxTransport: Yup.string().required("Required"),
      bandwidths: Yup.array().of(
        Yup.object({
          // allocatedBandwidth: Yup.string().test(
          //   "bandwidth-required-when-grx-yes",
          //   "Required",
          //   function (value) {
          //     const parent = this.from?.[1]?.value;
          //     if (parent?.isIpxGrxTransport !== "yes") {
          //       return true;
          //     }
          //     if (!value || !value.trim()) return false;
          //     return /^\d+$/.test(value);
          //   }
          // ).matches(/^\d+$/, "Numbers only"),
          allocatedBandwidth: Yup.string().test(
            "allocatedBandwidth-validation",
            function (value) {
              const parent = this.from?.[1]?.value;

              if (parent?.isIpxGrxTransport !== "yes") {
                return true;
              }

              if (!value || !value.trim()) {
                return this.createError({ message: "Required" });
              }
            
              if (!/^\d+(\.\d+)?$/.test(value)) {
                return this.createError({ message: "Numbers only" });
              }

              return true;
            }
          ),
        })
      ),
    }),
    IpxSigtran: Yup.object({
      isIpxSigtran: Yup.string().required("Required"),
      msus: Yup.array().of(
        Yup.object({
          // allocatedMsus: Yup.string().test(
          //   "msus-required-when-sigtran-yes",
          //   "Required",
          //   function (value) {
          //     const parent = this.from?.[1]?.value;
          //     if (parent?.isIpxSigtran !== "yes") {
          //       return true;
          //     }
          //     if (!value || !value.trim()) return false;
          //     return /^\d+$/.test(value);
          //   }
          // ).matches(/^\d+$/, "Numbers only"),
          allocatedMsus: Yup.string().test(
            "allocatedMsus-validation",
            function (value) {
              const parent = this.from?.[1]?.value;

              if (parent?.isIpxSigtran !== "yes") {
                return true;
              }

              if (!value || !value.trim()) {
                return this.createError({ message: "Required" });
              }

              if (!/^\d+(\.\d+)?$/.test(value)) {
                return this.createError({ message: "Numbers only" });
              }

              return true;
            }
          ),
        })
      ),
    }),
    IpxDiameter: Yup.object({
      isIpxDiameter: Yup.string().required("Required"),
      numberOfTransactions: Yup.array().of(
        Yup.object({
          // transaction: Yup.string().test(
          //   "numberOfTransactions-required-when-diameter-yes",
          //   "Required",
          //   function (value) {
          //     const parent = this.from?.[1]?.value;
          //     if (parent?.isIpxDiameter !== "yes") {
          //       return true;
          //     }
          //     if (!value || !value.trim()) return false;
          //     return /^\d+$/.test(value);
          //   }
          // ).matches(/^\d+$/, "Numbers only"),
          transaction: Yup.string().test(
            "transaction-validation",
            function (value) {
              const parent = this.from?.[1]?.value;

              if (parent?.isIpxDiameter !== "yes") {
                return true;
              }

              if (!value || !value.trim()) {
                return this.createError({ message: "Required" });
              }

              if (!/^\d+(\.\d+)?$/.test(value)) {
                return this.createError({ message: "Numbers only" });
              }

              return true;
            }
          ),
        })
      ),
    }),
    IpxHttp: Yup.object({
      isIpxHttp: Yup.string().required("Required"),
      transactionVolumes: Yup.array().of(
        Yup.object({
          allocatedTransactionVolumes: Yup.string().test(
            "allocatedTransactionVolumes-validation",
            function (value) {
              const parent = this.from?.[1]?.value;

              if (parent?.isIpxHttp !== "yes") {
                return true;
              }

              if (!value || !value.trim()) {
                return this.createError({ message: "Required" });
              }

              if (!/^\d+(\.\d+)?$/.test(value)) {
                return this.createError({ message: "Numbers only" });
              }

              return true;
            }
          ),
        })
      ),
    }),
    // IsIpxSigtran: Yup.string().required("Required"),
    // IsIpxDiameter: Yup.string().required("Required"),
    IsIpxDns: Yup.string().required("Required"),
  })
  .test(
    "at-least-one-service",
    "At least one service must be availed: IPX GRX/Transport, IPX Sigtran, IPX Http or IP Diameter.",
    function (sc) {
      const grxYes = sc?.IpxGrxTransport?.isIpxGrxTransport === "yes";
      const sigYes = sc?.IpxSigtran?.isIpxSigtran === "yes";
      const diaYes = sc?.IpxDiameter?.isIpxDiameter === "yes";
      const httpYes = sc?.IpxHttp?.isIpxHttp === "yes";
      const dnsYes = sc?.IsIpxDns === "yes";
      return grxYes || sigYes || diaYes || httpYes || dnsYes;
    }
  ),
  quotation: Yup.object({
    rows: Yup.array().of(
      Yup.object({
        ipxService: Yup.string(),
        group: Yup.string(),

        //Field-level validation that also understands row enable/disable
        setupCost: pricingFieldTest("setupCost"),
        monthlyCharges: pricingFieldTest("monthlyCharges"),
        usageBased: pricingFieldTest("usageBased"),
      })
    ),
  }),
});

// Option lists
export const currencyOptions = [
  { label: "Please Select", value: "", symbol: "" },
  { label: "EUR (€)", value: "EUR", symbol: "€" },
  { label: "USD ($)", value: "USD", symbol: "$" },
];

export const minimumContractPeriod = [
  { label: "Please Select", value: "" },
  { label: "12 Months", value: "12" },
  { label: "24 Months", value: "24" },
  { label: "36 Months", value: "36" },
];

export const connectionOptions = [
  { label: "Please Select", value: "" },
  { label: "Existing", value: "existing" },
  { label: "To Be Built", value: "toBeBuilt" },
  { label: "To Be Upgraded", value: "toBeUpgraded" },
  { label: "Other Plans Explain Under Notice", value: "otherPlansExplainUnderNotice" },
];

//  export const regionOptions = [
//   { label: "Please Select", value: "" },
//   { label: "Asia", value: "asia" },
//   { label: "Europe", value: "europe" },
//   { label: "North America", value: "na" },
//   { label: "Africa", value: "AFRICA" },
// ];

export const portSizeOptions = [
  { label: "Please Select", value: "" },
  { label: "1 GBPS", value: "1" },
  { label: "10 GBPS", value: "10" },
  { label: "20 GBPS", value: "20" },
  { label: "30 GBPS", value: "30" },
  { label: "40 GBPS", value: "40" },
  { label: "50 GBPS", value: "50" },
  { label: "60 GBPS", value: "60" },
  { label: "70 GBPS", value: "70" },
  { label: "80 GBPS", value: "80" },
  { label: "90 GBPS", value: "90" },
  { label: "100 GBPS", value: "100" },
];

export const grxTransportOptions = [
  { label: "Please Select", value: "" },
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];


export const bandwidthOptions = [
  { label: "Please Select", value: "" },
  { label: "20 Mbps", value: "20" },
  { label: "50 Mbps", value: "50" },
  { label: "100 Mbps", value: "100" },
  { label: "200 Mbps", value: "200" },
  { label: "500 Mbps", value: "500" },
  { label: "1000 Mbps", value: "1000" },
  { label: "2000 Mbps", value: "2000" },
  { label: "2500 Mbps", value: "2500" },
  { label: "3000 Mbps", value: "3000" },
  { label: "4000 Mbps", value: "4000" },
  { label: "5000 Mbps", value: "5000" },
  { label: "10000 Mbps", value: "10000" },
  { label: "20000 Mbps", value: "20000" },
  { label: "30000 Mbps", value: "30000" },
  { label: "40000 Mbps", value: "40000" },
  { label: "50000 Mbps", value: "50000" },
  { label: "60000 Mbps", value: "60000" },
  { label: "70000 Mbps", value: "70000" },
  { label: "80000 Mbps", value: "80000" },
  { label: "90000 Mbps", value: "90000" },
  { label: "100000 Mbps", value: "100000" },
];

export const TYPE = {
  PARENT:"parent",
  CHILD:"child"
}

export const ACCORDIAN_TITLE = {
  QUOT_SUMMARY:"Quotation",
  FLOOR_SUMMARY:"Floor Price",
  SERVICE_CONFIG:"Service Configuration",
  IPASC:"IPX Access Standard Connection",
  IPX_GRX_TRANSPORT:"IPX GRX/Transport",
  IPX_SIGTRAN:"IPX Sigtran",
  IPX_DIAMETER:"IPX Diameter",
  IPX_HTTP:"IPX HTTP2",
  IPX_DNS:"IPX DNS",
  QUOTATION:"Quotation",
  ORDER_FORM: "Order Form" //"Commercial Order Form IPX Services"
}

/* -------------------- Initial Values (new structure) -------------------- */
export const orderFormInitialValues = (organisationName,accountManagers) => ({
  vfPartyName: "Vodafone Enterprise Global Limited",
  customerName: organisationName ?? "",
  agreementStatus: {
    status:  "",
    signedDate: "",
  },
  // 1) Requested Service Commencement Date
  commencementDate: "",
  // 2) Minimum Term & Contacts
  termMonths: "",
  // 3) Minimum Term & Contacts
  technicalContact: "https://www.vodafone.com/business/carrier-terms", // URL (optional)
  accountManager: {
    name: accountManagers[0]?.name ?? "",
    title: "IPX Account Manager",
    mobile: "",
    email: accountManagers[0]?.email ?? "",
  },
  serviceOffering: {
    details: "",
  },
  // 5) Charges
  customerCharges: {
    nonRecurringCharge: "",
    monthlyCharge: "",
  },
  // 6) Installation Address (+ optional upload)
  installationAddress: {
    popAddress:"",
    popAddressImage: []
  },
  // 7) Additional Terms
  additionalTerms: `The parties acknowledge and agree that, with effect from the Service Commencement Date, this Order Form supersedes and terminates any and all Order Forms for this IPX Service previously entered into under the Agreement save with respect to any payment obligations.\nWhere the Customer wishes to change the total capacity of the Port at the installation address detailed in paragraph 5 above, it may do so in accordance with the terms of the Agreement. `,
  // 8) Vodafone Signatures
  vodafoneSignature: {
    legalEntity: "",
    registeredNumber: "",
    country: "",
    registeredAddress: "",
    name: "",
    title: "",
    date: "",
  },
  // 9) Customer Signatures
  customerSignature: {
    legalEntity: "",
    registeredNumber: "",
    country: "",
    registeredAddress: "",
    name:  "",
    title: "",
    date: "",
  },
});

export const DEMAND_ACTIONS = {
  INITIATE_FISIBILITY:'initiate-feasibiity',
  CPMPLETE_FEASIBILITY:'complete-feasibiity',
  INITIATE_FLOOR_PRICING:'initiate-floor-pricing',
  INITIATE_MARGIN:'initiate-margin',
  GENERATE_QUOTATION:'generate-quotation',
  REVIEW_QUOTATION:'review-quotation',
  APPROVE_QUOTATION:'approve-quotation',
  REJECT_QUOTATION:'reject-quotation',
  COMPLETE_DRAFT_ORDERFORM:'complete-draft-orderform',
  SAVE_ORDERFORM:'save-orderform',
  SUBMIT_SIGNED_ORDERFORM:"submit-signed-orderform",
  INITIATE_LEGAL_CONTRACT:"intiate-legal-contract",
  RESEND_LEGAL_CONTRACT:"resend-legal-contract",
  INITIATE_PROVISIONING:'initiate-provisioning',
  COMPLETE_PROVISIONING:'complete-provisioning',
  INITIATE_BILLING:'initiate-billing',
  COMPLETE_BILLING:'complete-billing',
  EDIT_SERVICE_START_DATE:"edit-service-activation-date",
  COMPLETE_DEMAND:'complete-demand',
  UPLOAD_LEGAL_CONTRACT:"upload-legal-contract",
  CLOSE_DEMAND:"close-demand",
}

export const ACTION_STATUS = {
  REVIEW_INITIATED:'REVIEW_INITIATED',
  FEASIBILITY_INITIATED:'FEASIBILITY_INITIATED',
  FEASIBILITY_COMPLETED:'FEASIBILITY_COMPLETED',
  QUOTATION_FLOOR_PRICING_INITIATED:'QUOTATION_FLOOR_PRICING_INITIATED',
  QUOTATION_MARGIN_INITIATED:'QUOTATION_MARGIN_INITIATED',
  QUOTATION_CUSTOMER_APPROVAL_INITIATED:'QUOTATION_CUSTOMER_APPROVAL_INITIATED',
  QUOTATION_CUSTOMER_APPROVAL_COMPLETED:'QUOTATION_CUSTOMER_APPROVAL_COMPLETED',
  ORDER_FORM_COMPLETED:'ORDER_FORM_COMPLETED',
  ORDER_FORM_UNSIGNED_SAVED_BY_CUSTOMER:"ORDER_FORM_UNSIGNED_SAVED_BY_CUSTOMER",
  ORDER_FORM_CUSTOMER_APPROVAL_INITIATED:"ORDER_FORM_CUSTOMER_APPROVAL_INITIATED",
  ORDER_FORM_CUSTOMER_APPROVAL_COMPLETED:'ORDER_FORM_CUSTOMER_APPROVAL_COMPLETED',
  ORDER_FORM_CUSTOMER_SIGNED_COPY_UPLOADED:"ORDER_FORM_CUSTOMER_SIGNED_COPY_UPLOADED",
  ORDER_FORM_LEGAL_CONTRACT_SENT:"ORDER_FORM_LEGAL_CONTRACT_SENT",
  PROVSIONING_INITIATED:'PROVSIONING_INITIATED',
  PROVSIONING_COMPLETED:'PROVSIONING_COMPLETED',
  BILLING_INITIATED:'BILLING_INITIATED',
  BILLING_ONBOARDING_COMPLETED:'BILLING_ONBOARDING_COMPLETED',
  DEMAND_COMPLETED:'DEMAND_COMPLETED',
  ORDER_FORM_CREATION_INITIATED:"ORDER_FORM_CREATION_INITIATED",
  DEMAND_CLOSED:"DEMAND_CLOSED",
  DEMAND_CLOSED_BY_SYSTEM:"DEMAND_CLOSED_BY_SYSTEM"
}

export const Quotation_SUBTAB = {
    FEASIBILITY: "FEASIBILITY",
    FLOOR_PRICING: "FLOOR_PRICING",
    QUOTATION: "QUOTATION",
}


export const WORKFLOW_STAGES = {
  DEMAND: "Demand",
  QUOTATION: "Quotation",
  ORDER_FORM: "Order Form",
  SERVICE_BUILDING: "Service Building",
  BILLING_ONBOARDING: "Billing Onboarding",
  GO_LIVE: "Go Live",
};

export const QUOTATION_CONFIG = {
  rejected: {
    title: "Quotation Rejected",
    variant: "error",
  },
  accepted: {
    title: "Quotation Accepted",
    variant: "success",
  },
  expired: {
    title: "Quotation Duration Ended",
    description: "Quotation Duration Ended",
    variant: "info",
  },
};
export const VALID_QUOTATION_STATUSES = ["rejected", "accepted", "expired"];

export const DEMAND_NOTES = {
  DEMAND_LIST:{
    account_manager_internal:{
      message:"This page shows a summary of all customer demands assigned to you, along with their latest stage, status, and key details. Click on any 'Demand ID' to view complete information and take required actions (If any)."
    },
    commercial_manager_internal:{
      message:"This page shows a summary of all customer demands assigned to you, along with their latest stage, status, and key details. Click on any 'Demand ID' to view complete information and take required actions (If any)."
    },
    engineering_or_technology_internal:{
      message:"This page shows a summary of all customer demands raised till now along with their latest stage, status, and key details. Click on any 'Demand ID' to view complete information."
    },
    demands_customer:{
      message:"This page shows a summary of all your submitted demands with their latest stage, status, and details. Click on any 'Demand ID' to view complete information and take required actions (If any)."
    },
    default:{
      message:"This page shows a summary of all customer demands raised till now along with their latest stage, status, and key details. Click on any 'Demand ID' to view complete information."
    },
  }
}

export const QUOTATION_STATUS = {
  REJECTED:"rejected", ACCEPTED:"accepted", EXPIRED:"expired"
};

export const STAGE_LABEL_MAP = {
  "Demand": 'Demand',
  "Quotation": 'Quotation',
  'Order Form': 'Order Form',
  'Service Building': 'Service Provisioning',
  'Billing Onboarding': 'Billing Onboarding',
  'Go Live': 'Demand Go Live'
}
export const EMAIL_DELIVERY_STATUS = Object.freeze({
  TRIGGERED: 'TRIGGERED',
  SENT: 'SENT',
  FAILED: 'FAILED'
})

export const QUOTATION_PDF_ALLOWED_STATUSES_FOR_CUSTOMER = ["active","accepted",];
export const getDownloadConfig = ({status,userType}) => {
  return {
    FLOOR_PRICING: {title: "Floor Price",canDownload: CAN(Action.DOWNLOAD_FLOORPRICE_PDF,Resource.D2C_DEMANDS),},
    QUOTATION: {title: "Quotation",
      canDownload:
        CAN(Action.DOWNLOAD_QUOTATION_PDF, Resource.D2C_DEMANDS) &&
        (userType===USER_TYPE.INTERNAL ||
          (userType===USER_TYPE.CUSTOMER &&
            QUOTATION_PDF_ALLOWED_STATUSES_FOR_CUSTOMER.includes(status))),
    },
  };
};

export const SELECTION_VALUES = {
  YES: "yes",
  NO: "no",
};